/**
 * Раздел «Инфраструктура» страницы «Аналитика» (US-04.7, источник данных —
 * мониторинг СНП): верхняя плашка из 5 показателей (чистая питьевая вода,
 * газоснабжение, интернет, дороги в норм. состоянии, общая протяжённость
 * дорог) + виджеты:
 *  - «Инженерная инфраструктура и связь» — наличие по СНП (лист 5,
 *    кол. 9/10/11/13/21): по типу — бар, % охвата и «N из M» сёл; у сотовой
 *    связи — чипы операторов; тип без данных (газ, кол. 20 пуста) —
 *    плашкой «нет данных» внизу. Охват по населению и прирост — ручной
 *    ввод (US-03), пока не отображаются;
 *  - «Водообеспечение» — доступ к чистой воде (крупный %, подпись
 *    «до норматива — X п.п. · N человек», шкала 0% → норматив 100%),
 *    «Структура водоснабжения» (стек-бар централизованное /
 *    децентрализованное, СНП лист 4 кол. 13/30), риск-плашка «Население
 *    на привозной воде» (кол. 31) и протяжённость водоразводящих сетей;
 *  - «Дорожная сеть» — областные дороги (мониторинг СНП, лист 7
 *    кол. 9–14): общая протяжённость + 3 состояния в км и % .
 *
 * Показатели плашки видны всегда (без данных — «—», замечание ревью);
 * строка / срез / виджет без данных не рендерится.
 *
 * Источник данных — гибрид: демо-КАТО (HARD_DATA_KATOS)
 * → мок hardData/analyticsInfrastructure.json (охват %, доли состояний
 * дорог и агрегаты плашки вычисляются на фронте); остальные КАТО
 * (по выбранному в сайдбаре) — один запрос /api/public/analytics/
 * infrastructure (DP-137/DP-164, US-04.7): plashka → плашка (агрегаты
 * считает бэк), engineering (covered_snp/total_snp/operators) →
 * «Инженерная инфраструктура и связь», water_supply → «Водообеспечение»,
 * roads (строка oblast) → «Дорожная сеть».
 */
import { Waypoints } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { StatCard } from '../../../components/widgets/StatCard';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import analyticsInfrastructure from '../../../hardData/analyticsInfrastructure.json';
import { getAnalyticsInfrastructure } from '../../../requests/analytics.request';
import { ModuleGate } from '../../../components/common/ModuleGate';
import { MODULES } from '../../../consts/modules';
import { useModulePresent } from '../../../hooks/useModulePresent';
import { useAnalyticsSection } from '../useAnalyticsSection';
import type { InfrastructureAnalyticsResponse } from '../../../types/analytics';
import { cn } from '../../../lib/cn';

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);
const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);

const pct = (v: number | null): string => (v !== null ? `${fmt1(v)}%` : '—');

interface InfrastructureSectionProps {
  /** Режим витрины «Все» (US-04.1): только ключевые виджеты — «Инженерная
   * инфраструктура и связь» и «Дорожная сеть» (FR-2.2). */
  summary?: boolean;
}

/** key инженерной строки API → ключ подписи engineering.rows.*
 * (mobile_internet → mobileInternet; legacy-ключи DP-268 — как есть). */
const ENGINEERING_KEYS: Record<string, string> = {
  electricity: 'electricity',
  cellular: 'cellular',
  mobile_internet: 'mobileInternet',
  broadband: 'broadband',
  gas: 'gas',
  water: 'water',
  internet: 'internet',
  mobile: 'mobile',
};

interface EngineeringRowVM {
  key: string;
  /** Охват по СНП, % — covered/total, иначе готовый snp_coverage_pct. */
  pct: number | null;
  covered: number | null;
  total: number | null;
  operators: Array<{ name: string; covered: number }> | null;
}

export const InfrastructureSection = ({
  summary = false,
}: InfrastructureSectionProps = {}) => {
  const { t } = useTranslation();
  // Плашка ключевых показателей — отдельный виджет витрины (US-09 FR-1).
  const showPlashka = useModulePresent(MODULES.analytics.infrastructurePlashka);

  const { data, loading, isHardKato } =
    useAnalyticsSection<InfrastructureAnalyticsResponse>(
      'infrastructure',
      getAnalyticsInfrastructure,
    );

  // --- Разрезы в общей форме: мок для демо-КАТО,
  // /analytics/infrastructure для остальных ---

  const coverage = (
    covered: number | null,
    total: number | null,
  ): number | null =>
    covered !== null && total !== null && total > 0
      ? (covered / total) * 100
      : null;

  const engineering: EngineeringRowVM[] = isHardKato
    ? analyticsInfrastructure.engineering.map((row) => ({
        key: row.key,
        pct: coverage(row.covered, row.total),
        covered: row.covered,
        total: row.total,
        operators: row.operators,
      }))
    : (data?.engineering ?? []).map((row) => {
        const covered = row.covered_snp ?? null;
        const total = row.total_snp ?? null;
        return {
          key: ENGINEERING_KEYS[row.key] ?? row.key,
          pct: coverage(covered, total) ?? row.snp_coverage_pct,
          covered,
          total,
          operators:
            row.operators?.map((operator) => ({
              name: operator.name,
              covered: operator.covered_snp ?? 0,
            })) ?? null,
        };
      });

  const waterSupply: {
    networkLength: number | null;
    cleanWaterAccess: number | null;
    centralizedPct: number | null;
    decentralizedPct: number | null;
    truckedPopulation: number | null;
    noAccessPopulation: number | null;
  } = isHardKato
    ? analyticsInfrastructure.waterSupply
    : {
        networkLength: data?.water_supply?.network_length_km ?? null,
        cleanWaterAccess: data?.water_supply?.clean_water_access_pct ?? null,
        centralizedPct: data?.water_supply?.centralized_water_pct ?? null,
        decentralizedPct: data?.water_supply?.decentralized_water_pct ?? null,
        truckedPopulation:
          data?.water_supply?.trucked_water_population ?? null,
        noAccessPopulation: data?.water_supply?.no_access_population ?? null,
      };

  // --- «Инженерная инфраструктура и связь»: наличие по СНП; тип без
  // данных — плашкой «нет данных» внизу виджета (макет: газ) ---
  const engineeringRows = engineering.filter(
    (row) => row.pct !== null || row.covered !== null,
  );
  const engineeringNoData = engineering.filter(
    (row) => row.pct === null && row.covered === null,
  );

  // --- «Дорожная сеть»: областные дороги (мониторинг СНП, лист 7) —
  // 3 состояния в км и %; км/% достраиваются друг из друга через total ---
  type RoadConditionKey = 'normative' | 'satisfactory' | 'bad';
  const roadCondition = (
    key: RoadConditionKey,
    km: number | null,
    pctValue: number | null,
    total: number | null,
  ): Array<{ key: RoadConditionKey; km: string; percent: string }> => {
    const kmKnown =
      km ?? (pctValue !== null && total !== null ? (pctValue / 100) * total : null);
    const pctKnown =
      pctValue ??
      (km !== null && total !== null && total > 0 ? (km / total) * 100 : null);
    if (kmKnown === null && pctKnown === null) return [];
    return [
      {
        key,
        km: kmKnown !== null ? fmt1(kmKnown) : '—',
        percent: pctKnown !== null ? fmt1(pctKnown) : '—',
      },
    ];
  };

  const oblastRoads: {
    total: number | null;
    conditions: Array<{ key: RoadConditionKey; km: string; percent: string }>;
  } | null = isHardKato
    ? (() => {
        const road = analyticsInfrastructure.roads.find(
          (item) => item.key === 'regional',
        );
        if (!road || road.total === null) return null;
        return {
          total: road.total,
          conditions: [
            ...roadCondition('normative', road.normative, null, road.total),
            ...roadCondition(
              'satisfactory',
              road.satisfactory,
              null,
              road.total,
            ),
            ...roadCondition('bad', road.bad, null, road.total),
          ],
        };
      })()
    : (() => {
        const road = (data?.roads?.roads ?? []).find(
          (item) => item.key === 'oblast',
        );
        if (!road) return null;
        return {
          total: road.total_km,
          conditions: [
            ...roadCondition(
              'normative',
              road.normative_km,
              road.normative_pct,
              road.total_km,
            ),
            ...roadCondition(
              'satisfactory',
              road.satisfactory_km,
              road.satisfactory_pct,
              road.total_km,
            ),
            ...roadCondition('bad', road.bad_km, road.bad_pct, road.total_km),
          ],
        };
      })();

  // --- Плашка (FR-2): демо-КАТО — агрегаты вычисляются из разрезов,
  // остальные — plashka ответа (агрегаты считает бэк) ---
  const mockRoadsKnown = analyticsInfrastructure.roads.filter(
    (
      road,
    ): road is (typeof analyticsInfrastructure.roads)[number] & {
      total: number;
    } => road.total !== null,
  );
  const mockRoadsTotal =
    mockRoadsKnown.length > 0
      ? mockRoadsKnown.reduce((acc, road) => acc + road.total, 0)
      : null;
  // Доля нормативных дорог — только по видам с известным состоянием
  // (вид без данных о состоянии не искажает знаменатель).
  const mockRoadsWithNormative = mockRoadsKnown.filter(
    (road) => road.normative !== null,
  );
  const mockRoadsNormative =
    mockRoadsWithNormative.length > 0
      ? (mockRoadsWithNormative.reduce(
          (acc, road) => acc + (road.normative ?? 0),
          0,
        ) /
          mockRoadsWithNormative.reduce((acc, road) => acc + road.total, 0)) *
        100
      : null;

  // Охват по СНП соответствующего типа — фолбэк плашки (охвата по
  // населению в мониторинге СНП нет, US-03 — ручной ввод).
  const engineeringPct = (...keys: string[]): number | null =>
    engineering.find((row) => keys.includes(row.key) && row.pct !== null)
      ?.pct ?? null;

  const plashka = data?.plashka;
  const cleanWater = isHardKato
    ? waterSupply.cleanWaterAccess
    : (plashka?.clean_water_pct ?? waterSupply.cleanWaterAccess);
  const gasCoverage = isHardKato
    ? engineeringPct('gas')
    : (plashka?.gas_coverage_pct ?? engineeringPct('gas'));
  const internetCoverage = isHardKato
    ? engineeringPct('mobileInternet', 'internet')
    : (plashka?.internet_coverage_pct ??
      engineeringPct('mobileInternet', 'internet'));
  const roadsNormative = isHardKato
    ? mockRoadsNormative
    : (plashka?.roads_normative_pct ?? data?.roads?.normative_pct ?? null);
  const roadsTotal = isHardKato
    ? mockRoadsTotal
    : (plashka?.roads_total_km ?? data?.roads?.total_km ?? null);

  // Плашка всегда показывает все показатели; нет данных → «—»
  // (замечание дизайн-ревью).
  const cards: Array<{ key: string; value: string; unit: string | null }> = [
    { key: 'cleanWater', value: pct(cleanWater), unit: null },
    { key: 'gas', value: pct(gasCoverage), unit: null },
    { key: 'internet', value: pct(internetCoverage), unit: null },
    { key: 'roadsNormative', value: pct(roadsNormative), unit: null },
    {
      key: 'roadsLength',
      value: roadsTotal !== null ? fmt(roadsTotal) : '—',
      unit: t('analytics.infrastructure.unitKm'),
    },
  ];

  const hasWaterSupply =
    waterSupply.networkLength !== null ||
    waterSupply.cleanWaterAccess !== null ||
    waterSupply.centralizedPct !== null ||
    waterSupply.decentralizedPct !== null ||
    waterSupply.truckedPopulation !== null;

  return (
    <section className="space-y-4" data-testid="analytics-infrastructure">
      <div className="flex items-center gap-4">
        <h2 className="shrink-0 text-xl font-extrabold">
          {t('analytics.infrastructure.title')}
        </h2>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <Waypoints
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {/* Пока грузятся данные с бэка — лоадер вместо виджетов. */}
      {loading ? (
        <BlockLoader />
      ) : (
        <>

      {/* Плашка: все показатели всегда видны, без данных — «—». */}
      {!summary && showPlashka && (
        <div className="flex flex-wrap gap-3.5">
          {cards.map((card) => (
            <div key={card.key} className="min-w-52 flex-1">
              <StatCard
                testId={`analytics-card-${card.key}`}
                title={t(`analytics.infrastructure.cards.${card.key}`)}
                value={card.value}
                unit={card.unit}
              />
            </div>
          ))}
        </div>
      )}

      {/* Виджеты всегда видны: без данных — заголовок + «Нет данных»
          (замечание дизайн-ревью). Инженерная инфраструктура и
          водообеспечение — одинаковой ширины (50/50, замечание ревью).
          На витрине «Все» водообеспечение скрыто — инженерная таблица
          занимает всю ширину без сетки. */}
      <div className={summary ? undefined : 'grid gap-3.5 xl:grid-cols-2'}>
        <ModuleGate id={MODULES.analytics.infrastructureEngineering}>
        <Card
          className="shadow-card"
          data-testid="analytics-infra-engineering"
        >
          <CardHeader>
            <CardTitle>
              {t('analytics.infrastructure.engineering.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {engineeringRows.length === 0 && engineeringNoData.length === 0 ? (
              <p data-testid="block-placeholder" className="text-sm lowercase text-text-muted">{t('common.noData')}</p>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3 px-3 pb-2 text-sm leading-tight text-text-faint">
                  <span>
                    {t('analytics.infrastructure.engineering.columnType')}
                  </span>
                  <span>
                    {t('analytics.infrastructure.engineering.columnSnp')}
                  </span>
                </div>
                <ul>
                  {engineeringRows.map((row) => (
                    <li
                      key={row.key}
                      data-testid={`analytics-infra-engineering-${row.key}`}
                      className="flex items-center gap-4 border-b border-surface-border px-3 py-4 text-sm last:border-b-0"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold leading-snug">
                          {t(
                            `analytics.infrastructure.engineering.rows.${row.key}`,
                          )}
                        </span>
                        {/* Уточнение «проводной интернет» — только у
                            широкополосного доступа (макет). */}
                        {row.key === 'broadband' && (
                          <span className="block text-xs leading-snug text-text-faint">
                            {t(
                              'analytics.infrastructure.engineering.broadbandHint',
                            )}
                          </span>
                        )}
                        {row.operators && row.operators.length > 0 && (
                          <span className="mt-1.5 flex flex-wrap gap-1.5">
                            {row.operators.map((operator) => (
                              <span
                                key={operator.name}
                                className="inline-flex rounded-full bg-surface-muted px-2.5 py-0.5 text-xs font-semibold text-text-muted"
                              >
                                {operator.name === 'other'
                                  ? t(
                                      'analytics.infrastructure.engineering.operatorOther',
                                    )
                                  : operator.name}{' '}
                                {fmt(operator.covered)}
                              </span>
                            ))}
                          </span>
                        )}
                      </span>
                      <span className="hidden h-2.5 w-36 shrink-0 overflow-hidden rounded-full bg-surface-muted sm:block">
                        {row.pct !== null && (
                          <span
                            className="block h-full rounded-full bg-brand-700"
                            style={{ width: `${Math.min(100, row.pct)}%` }}
                          />
                        )}
                      </span>
                      <span className="w-16 shrink-0 text-right">
                        <span className="block font-extrabold tabular-nums">
                          {pct(row.pct)}
                        </span>
                        {row.covered !== null && row.total !== null && (
                          <span className="block text-xs tabular-nums text-text-faint">
                            {t(
                              'analytics.infrastructure.engineering.ofTotal',
                              {
                                covered: fmt(row.covered),
                                total: fmt(row.total),
                              },
                            )}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                {/* Типы без данных (газ: лист 5 кол. 20 пуста). */}
                {engineeringNoData.map((row) => (
                  <div
                    key={row.key}
                    data-testid={`analytics-infra-engineering-${row.key}`}
                    className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-surface-muted/60 p-3 text-sm"
                  >
                    <span className="font-semibold leading-snug">
                      {t(
                        `analytics.infrastructure.engineering.rows.${row.key}`,
                      )}
                    </span>
                    <span className="shrink-0 lowercase text-text-faint">
                      — {t('common.noData')}
                    </span>
                  </div>
                ))}
              </>
            )}
          </CardContent>
        </Card>
        </ModuleGate>

        {!summary && (
          <ModuleGate id={MODULES.analytics.infrastructureWater}>
          <Card
            className="shadow-card"
            data-testid="analytics-infra-water"
          >
            <CardHeader>
              <CardTitle>
                {t('analytics.infrastructure.waterSupply.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {!hasWaterSupply && (
                <p data-testid="block-placeholder" className="text-sm lowercase text-text-muted">{t('common.noData')}</p>
              )}
              {waterSupply.cleanWaterAccess !== null && (
                <div data-testid="analytics-infra-water-access">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-bold leading-snug">
                        {t(
                          'analytics.infrastructure.waterSupply.cleanWaterAccess',
                        )}
                      </p>
                      {/* До норматива (100%) — п.п. и, когда бэк отдаёт,
                          человек без доступа. */}
                      {waterSupply.cleanWaterAccess < 100 && (
                        <p className="mt-0.5 text-xs leading-snug text-text-faint">
                          {t('analytics.infrastructure.waterSupply.belowNorm', {
                            pp: fmt1(100 - waterSupply.cleanWaterAccess),
                          })}
                          {waterSupply.noAccessPopulation !== null &&
                            ` · ${fmt(waterSupply.noAccessPopulation)} ${t('units.people')}`}
                        </p>
                      )}
                    </div>
                    <p className="shrink-0 text-2xl font-extrabold tabular-nums text-brand-600">
                      {fmt1(waterSupply.cleanWaterAccess)}
                      <span className="text-sm">&thinsp;%</span>
                    </p>
                  </div>
                  {/* Шкала к нормативу: засечка на 100% (макет). */}
                  <span className="relative mt-2 block h-2.5 w-full">
                    <span className="absolute inset-0 block overflow-hidden rounded-full bg-surface-muted">
                      <span
                        className={cn(
                          'block h-full rounded-full',
                          waterSupply.cleanWaterAccess >= 100
                            ? 'bg-brand-600'
                            : 'bg-status-warning',
                        )}
                        style={{
                          width: `${Math.min(100, waterSupply.cleanWaterAccess)}%`,
                        }}
                      />
                    </span>
                    <span
                      className="absolute -inset-y-0.5 right-0 block w-0.5 rounded-full bg-text-faint/60"
                      aria-hidden="true"
                    />
                  </span>
                  <div className="mt-1 flex items-center justify-between text-xs text-text-faint">
                    <span>0%</span>
                    <span>
                      {t('analytics.infrastructure.waterSupply.normScale')}
                    </span>
                  </div>
                </div>
              )}
              {waterSupply.networkLength !== null && (
                <div
                  data-testid="analytics-infra-water-network"
                  className="flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="font-bold leading-snug">
                      {t('analytics.infrastructure.waterSupply.networkLength')}
                    </p>
                    <p className="mt-0.5 text-xs leading-snug text-text-faint">
                      {t('analytics.infrastructure.waterSupply.networkHint')}
                    </p>
                  </div>
                  <p className="shrink-0 whitespace-nowrap text-2xl font-extrabold tabular-nums text-chart-indigo">
                    {fmt1(waterSupply.networkLength)}{' '}
                    <span className="text-sm font-normal text-text-muted">
                      {t('analytics.infrastructure.unitKm')}
                    </span>
                  </p>
                </div>
              )}
              {/* «Структура водоснабжения» (СНП лист 4, кол. 13/30):
                  стек-бар долей населения по типу источника. */}
              {(waterSupply.centralizedPct !== null ||
                waterSupply.decentralizedPct !== null) && (
                <div data-testid="analytics-infra-water-types">
                  <p className="font-bold leading-snug">
                    {t(
                      'analytics.infrastructure.waterSupply.structureTitle',
                    )}
                  </p>
                  <p className="mt-0.5 text-xs leading-snug text-text-faint">
                    {t('analytics.infrastructure.waterSupply.structureHint')}
                  </p>
                  <span className="mt-2 flex h-2.5 w-full overflow-hidden rounded-full bg-surface-muted">
                    {waterSupply.centralizedPct !== null && (
                      <span
                        className="block h-full bg-brand-700"
                        style={{ width: `${waterSupply.centralizedPct}%` }}
                      />
                    )}
                    {waterSupply.decentralizedPct !== null && (
                      <span
                        className="block h-full bg-brand-100"
                        style={{ width: `${waterSupply.decentralizedPct}%` }}
                      />
                    )}
                  </span>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                    {(
                      [
                        [
                          'centralized',
                          waterSupply.centralizedPct,
                          'bg-brand-700',
                        ],
                        [
                          'decentralized',
                          waterSupply.decentralizedPct,
                          'bg-brand-100',
                        ],
                      ] as Array<[string, number | null, string]>
                    ).map(([key, value, color]) =>
                      value === null ? null : (
                        <span
                          key={key}
                          className="flex items-center gap-1.5"
                          data-testid={`analytics-infra-water-${key}`}
                        >
                          <span
                            className={cn('size-2 shrink-0 rounded-sm', color)}
                            aria-hidden="true"
                          />
                          <span className="text-text-muted">
                            {t(
                              `analytics.infrastructure.waterSupply.${key}`,
                            )}
                          </span>
                          <span className="font-extrabold tabular-nums">
                            {pct(value)}
                          </span>
                        </span>
                      ),
                    )}
                  </div>
                </div>
              )}
              {/* Риск-индикатор привозной воды (СНП лист 4, кол. 31):
                  0 — зелёная точка, больше нуля — красная и красное
                  значение. */}
              {waterSupply.truckedPopulation !== null && (
                <div
                  data-testid="analytics-infra-water-trucked"
                  className="flex items-center justify-between gap-3 rounded-lg bg-brand-50 p-4"
                >
                  <div className="min-w-0">
                    <p className="font-bold leading-snug">
                      {t('analytics.infrastructure.waterSupply.trucked')}
                    </p>
                    <p className="mt-0.5 text-xs leading-snug text-text-faint">
                      {t('analytics.infrastructure.waterSupply.truckedHint')}
                    </p>
                  </div>
                  <p
                    className={cn(
                      'flex shrink-0 items-center gap-2 whitespace-nowrap text-sm font-extrabold tabular-nums',
                      waterSupply.truckedPopulation > 0 &&
                        'text-status-danger',
                    )}
                  >
                    <span
                      className={cn(
                        'size-2 shrink-0 rounded-full',
                        waterSupply.truckedPopulation > 0
                          ? 'bg-status-danger'
                          : 'bg-brand-500',
                      )}
                      aria-hidden="true"
                    />
                    {fmt(waterSupply.truckedPopulation)} {t('units.people')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
          </ModuleGate>
        )}
      </div>

      {/* «Дорожная сеть»: областные дороги — общая протяжённость +
          3 состояния в км и % (мониторинг СНП, лист 7). */}
      <ModuleGate id={MODULES.analytics.infrastructureRoads}>
      <Card className="shadow-card">
        <CardHeader className="flex-row items-baseline justify-between gap-4 space-y-0">
          <CardTitle>{t('analytics.infrastructure.roads.title')}</CardTitle>
          <span className="shrink-0 text-sm text-text-faint">
            {t('analytics.infrastructure.roads.subtitle')}
          </span>
        </CardHeader>
        <CardContent>
          {oblastRoads === null ? (
            <p data-testid="block-placeholder" className="text-sm lowercase text-text-muted">{t('common.noData')}</p>
          ) : (
            <div data-testid="analytics-infra-roads">
              <div className="flex items-center gap-3 px-3 pb-2 text-sm text-text-faint">
                <span className="min-w-0 flex-1" />
                <span className="w-24 shrink-0 text-right">
                  {t('analytics.infrastructure.roads.columnKm')}
                </span>
                <span className="w-16 shrink-0 text-right">
                  {t('analytics.infrastructure.roads.columnPercent')}
                </span>
              </div>
              {oblastRoads.total !== null && (
                <div
                  className="flex items-center gap-3 rounded-lg bg-surface-muted/60 p-3 text-sm font-extrabold"
                  data-testid="analytics-infra-roads-total"
                >
                  <span className="min-w-0 flex-1 leading-snug">
                    {t('analytics.infrastructure.roads.totalLabel')}
                  </span>
                  <span className="w-24 shrink-0 text-right tabular-nums">
                    {fmt1(oblastRoads.total)}
                  </span>
                  <span className="w-16 shrink-0" />
                </div>
              )}
              <ul>
                {oblastRoads.conditions.map((condition) => (
                  <li
                    key={condition.key}
                    data-testid={`analytics-infra-roads-${condition.key}`}
                    className="flex items-center gap-3 border-b border-surface-border px-3 py-4 text-sm last:border-b-0 last:pb-0"
                  >
                    <span className="min-w-0 flex-1 font-semibold leading-snug">
                      {t(`analytics.infrastructure.roads.${condition.key}`)}
                    </span>
                    <span className="w-24 shrink-0 text-right font-extrabold tabular-nums">
                      {condition.km}
                    </span>
                    <span
                      className={cn(
                        'w-16 shrink-0 text-right font-bold tabular-nums',
                        condition.key === 'normative'
                          ? 'text-brand-600'
                          : condition.key === 'satisfactory'
                            ? 'text-status-warning'
                            : 'text-status-danger',
                      )}
                    >
                      {condition.percent}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
      </ModuleGate>
        </>
      )}
    </section>
  );
};
